import { DocumentInfo, DocumentInfoResponse, DocumentContent, InteractiveDocumentContent, QueryString, Controllers } from "@infoland/iprova-api";
import { AnchorType } from "@infoland/iprova-api";
import { OfficeOnlineParams } from "../../types/OfficeOnlineParams";
import { Store } from "vuex";
import { QuickCodeInfo } from "@/types/document/quickCodeInfo";
import { DocumentVersionInfo } from "@/types/document/documentVersionInfo";
import { ActionResult, namespace as documentNamespace } from "../modules/document/document";
import { Comment } from "@/documentComments";
import { CommentReply } from "@/types/commentReply";
import { DocumentResult } from "@/types/document/document";
import { DocumentVersionState, CommentState, DocumentCommentListMode } from "@/enums";
import { DocumentCheck } from "@/types/document/checkDocument";
import { DocumentTypeParagraphsResult } from "@/types/document/documentTypeParagraph";
import { CommentFilter } from "@/types/document/commentFilter";

export class DocumentStore {
    private store: Store<any>;

    constructor(store: Store<any>) {
        this.store = store;
    }

    /** Gets the document info  */
    public get documentInfo(): DocumentVersionInfo {
        return this.store.getters[`${documentNamespace}/documentInfo`];
    }

    //Move to documentComments store
    /** Gets the document comments info  */
    public get documentComments(): Comment[] {
        return this.store.getters[`${documentNamespace}/documentComments`];
    }

    /** Gets the document comment filters*/
    public get commentFilters(): CommentFilter[] {
        return this.store.getters[`${documentNamespace}/commentFilters`];
    }

    /** Loads the document */
    public loadDocumentAsync(documentId: string): Promise<DocumentInfoResponse> {
        return this.store.dispatch(`${documentNamespace}/loadDocumentAsync`, documentId);
    }

    /**Loads the document by documentId and state */
    public loadDocumentByStateAsync(
        documentId: string,
        state: DocumentVersionState,
        includePrintForcedHeader: boolean = false
    ): Promise<DocumentResult> {
        return this.store.dispatch(`${documentNamespace}/loadDocumentByStateAsync`, { documentId, state, includePrintForcedHeader });
    }

    public loadDocumentByVersionAsync(documentId: string, versionNumber: number): Promise<DocumentResult> {
        return this.store.dispatch(`${documentNamespace}/loadDocumentByVersionAsync`, { documentId, versionNumber });
    }

    /** Gets the document content */
    public getDocumentContentAsync(documentId: string, queryString?: QueryString): Promise<DocumentContent | InteractiveDocumentContent> {
        return this.store.dispatch(`${documentNamespace}/getDocumentContentAsync`, { documentId, queryString });
    }

    /** Gets the document content by documentId and state */
    public getDocumentContentByStateAsync(
        documentId: string,
        state: string,
        queryString?: QueryString
    ): Promise<DocumentContent | InteractiveDocumentContent> {
        return this.store.dispatch(`${documentNamespace}/getDocumentContentByStateAsync`, { documentId, state, queryString });
    }

    /** Gets the document embedded view*/
    public getEmbeddedViewAsync(documentId: string, documentVersion: number): Promise<OfficeOnlineParams> {
        return this.store.dispatch(`${documentNamespace}/getEmbeddedViewAsync`, { documentId, documentVersion });
    }

    /** Gets office online fillable form param*/
    public getOfficeOnlineFilleableFormParamsAsync(documentId: string, documentVersion: number): Promise<OfficeOnlineParams> {
        return this.store.dispatch(`${documentNamespace}/getOfficeOnlineFilleableFormParamsAsync`, { documentId, documentVersion });
    }

    /** Gets office online favicon*/
    public getOfficeOnlineFaviconAsync(documentId: string, documentVersion: number): Promise<string> {
        return this.store.dispatch(`${documentNamespace}/getOfficeOnlineFaviconAsync`, { documentId, documentVersion });
    }

    public getQuickCodeInfoAsync(quickCode: string): Promise<QuickCodeInfo> {
        return this.store.dispatch(`${documentNamespace}/getQuickCodeInfoAsync`, quickCode);
    }

    public async loadDocumentVersionIncludingSpecificUsersAsync(
        documentId: string,
        version: number,
        includeWritersGroup: boolean,
        includeDocumentAdministrators: boolean,
        includeAuthors: boolean,
        includeAuthorizers: boolean,
        includeInvitedWriters: boolean,
        includeCheckTaskDelegatedToUser: boolean
    ): Promise<DocumentVersionInfo> {
        const documentVersionInfo = await Controllers.GenericController.getAsync<DocumentVersionInfo>(
            `/documents/${documentId}/v${version}?include_writers_group=${includeWritersGroup}&include_document_administrators=${includeDocumentAdministrators}&include_authors=${includeAuthors}&include_authorizers=${includeAuthorizers}&include_invited_writers=${includeInvitedWriters}&include_check_task_delegated_to_user=${includeCheckTaskDelegatedToUser}`
        );
        return documentVersionInfo;
    }

    public async loadDocumentVersionInfo(documentId: string, version: number): Promise<DocumentVersionInfo> {
        return await Controllers.GenericController.getAsync<DocumentVersionInfo>(`/documents/${documentId}/v${version}`);
    }

    public async checkDocument(documentId: string, documentVersion: number, documentCheck: DocumentCheck): Promise<string> {
        return await Controllers.GenericController.postAsync<DocumentCheck, string>(
            `/documents/${documentId}/v${documentVersion}/checks`,
            documentCheck
        );
    }

    /** Saves the comment */
    public async addCommentAsync(comment: Comment, documentId: string, version: number): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/addCommentAsync`, {
            documentId,
            version,
            comment
        });
    }

    /** Gets the document type paragraphs*/
    public getDocumentTypeParagraphsAsync(documentTypeId: number): Promise<DocumentTypeParagraphsResult> {
        return this.store.dispatch(`${documentNamespace}/getDocumentTypeParagraphsAsync`, documentTypeId);
    }

    /** Send the E-mail Document*/
    public async sendMailDocumentAsync(
        document_id: string,
        version: string,
        emailSubjectText: string,
        emailMessage: string,
        userIds: string[]
    ): Promise<boolean | null> {
        return this.store.dispatch(`${documentNamespace}/sendMailDocumentAsync`, { document_id, version, emailSubjectText, emailMessage, userIds });
    }

    /** get document comments for state */
    getDocumentCommentsAsync(documentId: string, documentState: DocumentVersionState): Promise<Comment[]> {
        return this.store.dispatch(`${documentNamespace}/getDocumentCommentsAsync`, {
            documentId,
            documentState
        });
    }

    /** get document comments for version */
    getDocumentCommentsByVersionAsync(documentId: string, version: number): Promise<Comment[]> {
        return this.store.dispatch(`${documentNamespace}/getDocumentCommentsByVersionAsync`, { documentId, version });
    }

    /** Delete a document comment */
    deleteCommentAsync(comment_id: number): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/deleteCommentAsync`, { comment_id, delete_value: true });
    }

    /** Undelete a document comment */
    undeleteCommentAsync(comment_id: number): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/undeleteCommentAsync`, { comment_id, delete_value: false });
    }

    /** Add a reply to a document comment */
    addCommentReplyAsync(comment_id: number, commentReply: CommentReply): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/addCommentReplyAsync`, { comment_id, commentReply });
    }

    /** Clear document comments from store */
    clearDocumentComments(): void {
        this.store.dispatch(`${documentNamespace}/clearDocumentComments`);
    }
    /** Edit a reply of a document comment */
    editCommentReplyAsync(comment_id: number, commentReply: CommentReply): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/editCommentReplyAsync`, { comment_id, commentReply });
    }
    /** Toggle a reply window of a document comment */
    toggleCommentReply(comment_id: number, comment_reply_id: number): void {
        this.store.dispatch(`${documentNamespace}/toggleCommentReply`, { comment_id, comment_reply_id });
    }

    /** Edit a document comment */
    editCommentAsync(
        documentId: string,
        documentVersion: number,
        comment_id: number,
        commentText: string,
        anchorId: string,
        anchorText: string,
        anchorType: AnchorType
    ): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/editCommentAsync`, {
            documentId,
            documentVersion,
            comment_id,
            commentText,
            anchorId,
            anchorText,
            anchorType
        });
    }

    /** update status of a document comment */
    async updateDocumentCommentState(commentState: CommentState, commentId: number): Promise<ActionResult<void, string>> {
        return this.store.dispatch(`${documentNamespace}/updateDocumentCommentState`, { commentState, commentId });
    }
}
